import {
  AlertTriangleIcon,
  ArrowUpIcon,
  MinusIcon,
  ArrowDownIcon,
  CircleDashedIcon,
  CheckSquareIcon,
  BugIcon,
  BookOpenIcon,
  FlagIcon,
  GitBranchIcon,
  ScanTextIcon,
  SparklesIcon,
  UserCheckIcon,
  type LucideIcon,
} from 'lucide-react';
import { ClassificationMethod, ComplexityLevel, ImpactLevel, BusinessSegment } from '@/shared/types/api';
import { TaskStatus, TaskPriority, TaskType } from '@/types/task';

/**
 * Single source of truth for classification / status presentation.
 * Colors live in CSS-variable token classes (index.css: .sev-*, .status-*),
 * so every screen renders identically and both themes are correct by construction.
 *
 * Axis separation (from the UX research): Impact carries a red→emerald HUE heat
 * scale; Complexity is a monochrome 1–5 fill glyph so the two never collide.
 */

// ---------- Impact (hue heat scale) ----------
export interface ImpactMeta {
  label: string;
  /** severity token class from index.css */
  sev: string;
  icon: LucideIcon;
}

export const impactFallback: ImpactMeta = {
  label: 'Unclassified',
  sev: 'sev-none',
  icon: CircleDashedIcon,
};

export const impactMeta: Record<string, ImpactMeta> = {
  [ImpactLevel.Critical]: { label: 'Critical', sev: 'sev-critical', icon: AlertTriangleIcon },
  [ImpactLevel.High]: { label: 'High', sev: 'sev-high', icon: ArrowUpIcon },
  [ImpactLevel.Medium]: { label: 'Medium', sev: 'sev-medium', icon: MinusIcon },
  [ImpactLevel.Low]: { label: 'Low', sev: 'sev-low', icon: ArrowDownIcon },
};

export const getImpactMeta = (v?: ImpactLevel | string | null): ImpactMeta =>
  (v && impactMeta[v]) || impactFallback;

// ---------- Complexity (monochrome 1–5 fill glyph) ----------
export interface ComplexityMeta {
  label: string;
  /** 1 (simplest) … 5 (most complex) — drives the segmented bar glyph */
  level: number;
}

export const complexityMeta: Record<string, ComplexityMeta> = {
  [ComplexityLevel.Simple]: { label: 'Simple', level: 1 },
  [ComplexityLevel.Routine]: { label: 'Routine', level: 2 },
  [ComplexityLevel.Moderate]: { label: 'Moderate', level: 3 },
  [ComplexityLevel.Complex]: { label: 'Complex', level: 4 },
  [ComplexityLevel.Critical]: { label: 'Critical', level: 5 },
};

export const COMPLEXITY_SEGMENTS = 5;

export const getComplexityMeta = (v?: ComplexityLevel | string | null): ComplexityMeta | null =>
  (v && complexityMeta[v]) || null;

// ---------- Business segment (distinct hue per segment, provenance via border) ----------
export interface SegmentMeta {
  label: string;
  /** .seg-* color token class from index.css */
  seg: string;
  tooltip?: string;
}

export const segmentFallback: SegmentMeta = {
  label: 'Unknown segment',
  seg: 'seg-none',
};

export const segmentMeta: Record<BusinessSegment, SegmentMeta> = {
  [BusinessSegment.Technical]: { label: 'Technical', seg: 'seg-technical', tooltip: 'Technical / platform' },
  [BusinessSegment.CIB]: { label: 'CIB', seg: 'seg-cib', tooltip: 'Corporate & Institutional Banking' },
  [BusinessSegment.AM]: { label: 'AM', seg: 'seg-am', tooltip: 'Asset Management' },
  [BusinessSegment.AS]: { label: 'AS', seg: 'seg-as', tooltip: 'Asset Servicing' },
  [BusinessSegment.Corp]: { label: 'Corp', seg: 'seg-corp', tooltip: 'Corporate / commercial' },
};

export const getSegmentMeta = (v?: BusinessSegment | string | null): SegmentMeta =>
  (v && segmentMeta[v as BusinessSegment]) || segmentFallback;

// ---------- Classification method (provenance, no color channel of its own) ----------
export interface ClassificationMethodMeta {
  label: string;
  icon: LucideIcon;
  /**
   * What this tier's confidence number actually measures. The tiers compute it in
   * incompatible ways, so "70%" does not mean the same thing across two rows — say so
   * rather than letting the bare percentage imply a single scale.
   */
  confidenceMeaning: string;
}

export const classificationMethodFallback: ClassificationMethodMeta = {
  label: 'Unknown',
  icon: CircleDashedIcon,
  confidenceMeaning: 'confidence scale unknown for this method',
};

export const classificationMethodMeta: Record<ClassificationMethod, ClassificationMethodMeta> = {
  Keyword: {
    label: 'Keyword',
    icon: ScanTextIcon,
    // KeywordClassificationService: top.Score / (top.Score + runnerUp.Score), or a flat 0.5
    // for a single unopposed match (which that code itself calls weak).
    confidenceMeaning: 'how far the winning keyword rule outscored the runner-up; 50% means a single unopposed match',
  },
  LLM: {
    label: 'LLM',
    icon: SparklesIcon,
    // LLMClassificationService.NormalizeConfidence buckets the model's own word into
    // 0.9/0.7/0.5, and anything it does not recognise also lands on 0.7.
    confidenceMeaning: "the model's own high/medium/low rating, bucketed to 90/70/50%; 70% also covers a rating the model did not give in those words",
  },
  Manual: {
    label: 'Manual',
    icon: UserCheckIcon,
    confidenceMeaning: 'set by a person, so always 100%',
  },
  // Defensive only. ClassificationJob applies a suggestion (and so writes classificationMetadata)
  // when the method is LLM or the confidence clears ApplyThreshold; the Default suggestion is
  // Method=Default at 0.3, which does not clear the shipped 0.6 threshold, so with the default
  // configuration this value is never stored and this branch never renders. It is kept for rows
  // written under a lowered ApplyThreshold, and so an unexpected value degrades to a named chip
  // rather than "Unknown".
  Default: {
    label: 'Fallback',
    icon: CircleDashedIcon,
    confidenceMeaning: 'a fixed placeholder, not a measurement — the classifier produced no usable verdict',
  },
};

export const getClassificationMethodMeta = (
  v?: ClassificationMethod | string | null
): ClassificationMethodMeta =>
  (v && classificationMethodMeta[v as ClassificationMethod]) || classificationMethodFallback;

// ---------- Task status (status owns hue across the board) ----------
export interface StatusMeta {
  label: string;
  /** .status-* pill class */
  pill: string;
  /** .dot-* class */
  dot: string;
  /** bare token name for column accents, e.g. 'inprogress' */
  key: string;
}

export const taskStatusMeta: Record<TaskStatus, StatusMeta> = {
  [TaskStatus.Todo]: { label: 'To Do', pill: 'status-todo', dot: 'dot-todo', key: 'todo' },
  [TaskStatus.InProgress]: { label: 'In Progress', pill: 'status-inprogress', dot: 'dot-inprogress', key: 'inprogress' },
  [TaskStatus.InReview]: { label: 'In Review', pill: 'status-inreview', dot: 'dot-inreview', key: 'inreview' },
  [TaskStatus.Done]: { label: 'Done', pill: 'status-done', dot: 'dot-done', key: 'done' },
  [TaskStatus.Blocked]: { label: 'Blocked', pill: 'status-blocked', dot: 'dot-blocked', key: 'blocked' },
  [TaskStatus.Cancelled]: { label: 'Cancelled', pill: 'status-cancelled', dot: 'dot-cancelled', key: 'cancelled' },
};

/** Board column order (Cancelled shown last, can be filtered out). */
export const taskStatusOrder: TaskStatus[] = [
  TaskStatus.Todo,
  TaskStatus.InProgress,
  TaskStatus.InReview,
  TaskStatus.Done,
  TaskStatus.Blocked,
  TaskStatus.Cancelled,
];

// ---------- Task priority (monochrome 1–5 level glyph) ----------
export const taskPriorityMeta: Record<TaskPriority, { label: string; level: number }> = {
  [TaskPriority.Lowest]: { label: 'Lowest', level: 1 },
  [TaskPriority.Low]: { label: 'Low', level: 2 },
  [TaskPriority.Medium]: { label: 'Medium', level: 3 },
  [TaskPriority.High]: { label: 'High', level: 4 },
  [TaskPriority.Highest]: { label: 'Highest', level: 5 },
};

// ---------- Task type (lucide icon, no cross-platform emoji) ----------
export const taskTypeMeta: Record<TaskType, { label: string; icon: LucideIcon }> = {
  [TaskType.Task]: { label: 'Task', icon: CheckSquareIcon },
  [TaskType.Bug]: { label: 'Bug', icon: BugIcon },
  [TaskType.Story]: { label: 'Story', icon: BookOpenIcon },
  [TaskType.Epic]: { label: 'Epic', icon: FlagIcon },
  [TaskType.SubTask]: { label: 'Sub-task', icon: GitBranchIcon },
};
